import * as React from 'react';
import { useLocation } from "react-router-dom";
import Page from "../../components/layout/Page";
import { CLIENT_ROUTE, CLIENT_TITLE, API_ROUTE } from "./constants";
import axios from "axios";
import { IResponse } from "../../types/api";
import { ICustomer, SocialPlatform } from "./types";
import {
    Avatar, Box,
    Container,
    createStyles,
    Flex,
    Grid,
    Indicator,
    Paper,
    Stack,
    Table,
    Text,
    useMantineTheme
} from "@mantine/core";
import {
    IconUserCircle,
    IconMail,
    IconPhone,
    IconBrandFacebook,
    IconBrandGoogle,
    IconBrandApple, IconCalendarTime, IconPlayerPlay, IconPlayerStop, IconTags
} from "@tabler/icons-react";
import dayjs from "dayjs";
import { last } from "ramda";
import { paymentTypes } from "../user-subscriptions/constants";

const useStyles = createStyles((theme) => {
    // console.log('theme>>>', theme);
    return {

        paper: {
            // border: `1px solid ${theme.other.gradientFrom}`,
            // maxHeight: '100%',
            // overflow: 'hidden',
            background: theme.fn.gradient({
                from: theme.other.gradientFrom,
                to: theme.other.gradientTo,
                deg: 270,
            })
        },
        detailItem: {
            display: 'flex',
            alignItems: 'center',
            border: '1px solid',
            borderColor: theme.other.gradientFrom,
            padding: '.3rem 0',
            borderRadius: 6,
            '> div': {
                '&:first-of-type': {
                    padding: `0 ${theme.spacing.xs}px`,
                    display: 'flex',
                    placeItems: 'center',
                },
                '&:last-of-type': {
                    flex: 1,
                    lineHeight: 1,
                }
            },
        },
        th: {
            borderColor: `${theme.other.gradientFrom} !important`,
            // color: `${'#d8e0bb'} !important`
        },
        td: {
            borderColor: `${theme.other.gradientFrom} !important`
        }
    }
})

interface DetailItemProps {
    label: string;
    value: React.ReactNode;
    icon: React.ReactNode;
}

const iconConfig = {
    stroke: 1,
    size: 28,
} as const;

const DATE_FORMAT = 'YYYY/MM/DD HH:mm:ss'

const DetailItem = ({
    label,
    value,
    icon
}: DetailItemProps) => {
    const { classes } = useStyles();
    return (
        <div className={classes.detailItem}>
            <div>
                {icon}
            </div>
            <div>
                <Text c='gray.7' fz={12}>
                    {label}
                </Text>
                <Text inherit fz={15}>
                    {value}
                </Text>
            </div>
        </div>
    )
}

const Detail = () => {

    const { state: id = null } = useLocation();
    const { classes } = useStyles();
    const theme = useMantineTheme();
    const iconColor = theme.other.gradientFrom;

    const [ customer, setCustomer ] = React.useState<ICustomer | null>(null);

    React.useEffect(() => {
        const fetchData = async () => {
            const { data } = await axios.get<IResponse<ICustomer>>(`${API_ROUTE}/${id}`)
            setCustomer(data.result ?? null);
        }
        void fetchData();
    }, []);

    const socialAccounts = React.useMemo(() => {
        let result: Partial<Record<SocialPlatform, string>> = {};
        const keys = Object.values(SocialPlatform);
        for (const socialAccount of (customer?.socialAccounts ?? []) ) {
            result[socialAccount.platform] = socialAccount.reference ?? '-';
        }
        return result;
    }, [customer]);

    const activeSubscription = React.useMemo(() => {
        return last(customer?.activeSubscriptions ?? []);
    }, [customer])

    return (
        <Page>
            <Page.Header title={CLIENT_TITLE} backRoute={CLIENT_ROUTE}/>
            <Page.Body>
                <Paper shadow="xs" radius={6} p={1} m={1} className={classes.paper}>
                    <Box style={{ background: 'white', borderRadius: 6 }} p='xl'>
                        <Container maw={900}>
                            <Flex
                                gap="md"
                                justify="center"
                                align="center"
                                direction="column"
                            >
                                <Indicator color={customer?.isActive ? 'green' : 'gray'} dot inline size={16} offset={12} position="bottom-end" withBorder>
                                    <Avatar
                                        size={80}
                                        src={customer?.profilePicture}
                                        style={{ borderRadius: '50%' }}
                                        alt={customer?.name}
                                    />
                                </Indicator>
                                <Text fw={500} fz={18}>
                                    {customer?.name ?? '-'}
                                </Text>
                            </Flex>
                            <Grid>
                                <Grid.Col span='auto'>
                                    <Text c='gray.7' fz={15} align='center'>
                                        Хэрэглэгчийн мэдээлэл
                                    </Text>
                                    <Stack spacing='xs'>
                                        <DetailItem
                                            label={'Хэрэглэгчийн ID'}
                                            value={customer?.customerId ?? '-'}
                                            icon={<IconUserCircle color={iconColor} size={iconConfig.size} stroke={iconConfig.stroke}/>}
                                        />
                                        <DetailItem
                                            label={'И-мэйл хаяг'}
                                            value={customer?.email ?? '-'}
                                            icon={<IconMail color={iconColor} size={iconConfig.size} stroke={iconConfig.stroke}/>}
                                        />
                                        <DetailItem
                                            label={'Утасны дугаар'}
                                            value={customer?.phoneNumber ?? '-'}
                                            icon={<IconPhone color={iconColor} size={iconConfig.size} stroke={iconConfig.stroke}/>}
                                        />
                                        <DetailItem
                                            label={'Сүүлд нэвтэрсэн огноо'}
                                            value={customer?.lastLoggedIn ? dayjs(customer.lastLoggedIn).format(DATE_FORMAT) : '-'}
                                            icon={<IconCalendarTime color={iconColor} size={iconConfig.size} stroke={iconConfig.stroke}/>}
                                        />
                                    </Stack>
                                </Grid.Col>
                                <Grid.Col span='auto'>
                                    <Text c='gray.7' fz={15} align='center'>
                                        Сошиал хаягууд
                                    </Text>
                                    <Stack spacing='xs'>
                                        <DetailItem
                                            label={'Facebook'}
                                            value={socialAccounts.facebook ?? '-'}
                                            icon={<IconBrandFacebook color={iconColor} size={iconConfig.size} stroke={iconConfig.stroke}/>}
                                        />
                                        <DetailItem
                                            label={'Google'}
                                            value={socialAccounts.google ?? '-'}
                                            icon={<IconBrandGoogle color={iconColor} size={20} stroke={2} style={{ marginLeft: 3, marginRight: 3 }}/>}
                                        />
                                        <DetailItem
                                            label={'Apple'}
                                            value={socialAccounts.apple ?? '-'}
                                            icon={<IconBrandApple color={iconColor} size={iconConfig.size} stroke={iconConfig.stroke}/>}
                                        />
                                    </Stack>
                                </Grid.Col>
                                <Grid.Col span='auto'>
                                    <Text c='gray.7' fz={15} align='center'>
                                        Идэвхитэй багц
                                    </Text>
                                    <Stack spacing='xs'>
                                        <DetailItem
                                            label={'Багцын нэр'}
                                            value={activeSubscription?.activatedSubscription.title}
                                            icon={<IconTags color={iconColor} size={iconConfig.size} stroke={iconConfig.stroke}/>}
                                        />
                                        <DetailItem
                                            label={'Эхлэх огноо'}
                                            value={activeSubscription?.startDate ? dayjs(activeSubscription?.startDate).format(DATE_FORMAT) : '-'}
                                            icon={<IconPlayerPlay color={iconColor} size={iconConfig.size} stroke={iconConfig.stroke}/>}
                                        />
                                        <DetailItem
                                            label={'Дуусах огноо'}
                                            value={activeSubscription?.endDate ? dayjs(activeSubscription?.endDate).format(DATE_FORMAT) : '-'}
                                            icon={<IconPlayerStop color={iconColor} size={iconConfig.size} stroke={iconConfig.stroke}/>}
                                        />
                                    </Stack>
                                </Grid.Col>
                            </Grid>
                            <Text mt='sm' mb='xs' c='gray.7' fz={17} fw={500} ml='sm'>
                                Худалдан авалтын түүх
                            </Text>
                            <Table withBorder fontSize='sm' style={{ borderColor: iconColor }} withColumnBorders>
                                <thead>
                                <tr>
                                    <th className={classes.th}>Багц</th>
                                    <th className={classes.th}>Эхлэх огноо</th>
                                    <th className={classes.th}>Дуусах огноо</th>
                                    <th className={classes.th}>Төлбөрийн хэлбэр</th>
                                </tr>
                                </thead>
                                <tbody>
                                {
                                    (customer?.purchaseHistories ?? []).map(sub => {
                                        return (
                                            <tr key={sub._id}>
                                                <td className={classes.td}>
                                                    {sub?.activatedSubscription?.title ?? '-'}
                                                </td>
                                                <td className={classes.td}>
                                                    {sub?.startDate ? dayjs(sub.startDate).format(DATE_FORMAT) :'-'}
                                                </td>
                                                <td className={classes.td}>
                                                    {sub?.endDate ? dayjs(sub.endDate).format(DATE_FORMAT) :'-'}
                                                </td>
                                                <td className={classes.td}>
                                                    {
                                                        paymentTypes?.[sub?.paidBy] ?? ''
                                                    }
                                                </td>
                                            </tr>
                                        )
                                    })
                                }
                                </tbody>
                            </Table>
                        </Container>
                    </Box>
                </Paper>
            </Page.Body>
        </Page>
    )
};

export default Detail;